import { OrgSectionView } from '@today/ui-domain'
import { useTranslation } from 'react-i18next'
import { organizationCapabilities } from '@/auth/organization-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useOrgUrlState } from '@/hooks/organization/useOrgUrlState'
import { ORGANIZATION_NAMESPACE } from '@/i18n'
import type { TenantConfigSectionProps } from '../section-props'
import { TypesTab } from './organization/TypesTab'
import { UnitsTab } from './organization/UnitsTab'

/**
 * The console's `organization` section (TO-389 organization-admin-ui): the unit
 * tree + detail and the unit-type editor. On the platform plane it renders a
 * notice before any data hook mounts, so no organization request is sent there
 * (the API scopes every call to the caller's own tenant).
 */
export function OrganizationSection({ tenantSlug, plane, readOnly }: TenantConfigSectionProps) {
	const { t } = useTranslation(ORGANIZATION_NAMESPACE)
	const { permissions } = useViewerPermissions()
	const caps = organizationCapabilities({ plane, readOnly, permissions })
	const url = useOrgUrlState()
	const tabs = [
		...(caps.tabs.units
			? [{ id: 'units' as const, content: <UnitsTab caps={caps} slug={tenantSlug} url={url} /> }]
			: []),
		...(caps.tabs.types ? [{ id: 'types' as const, content: <TypesTab canWrite={caps.write.types} /> }] : []),
	]
	const activeTab = tabs.some((tab) => tab.id === url.tab) ? url.tab : (tabs[0]?.id ?? 'units')
	let mode: 'notice' | 'forbidden' | 'tabs' = 'tabs'
	if (caps.platformNotice) mode = 'notice'
	else if (caps.forbidden) mode = 'forbidden'
	return <OrgSectionView t={t} mode={mode} tabs={tabs} activeTab={activeTab} onTabChange={url.setTab} />
}
